import {
  ChangeEventHandler,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ShareTemplateMain, UserTextArea } from "./Style";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Button,
  Image,
  Modal,
  Switch,
  Tooltip,
  Typography,
  message,
  notification,
} from "antd";
import { ProfileIcon } from "@/components/layout/MainHeader";
import { useSession } from "next-auth/react";
import { CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import {
  useCreateCommentMutation,
  useGetCommentQuery,
} from "@/redux/api/projectApi";
import ShareComment from "./ShareComment";
import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { useRouter } from "next/router";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { dashboardActions } from "@/redux/stores/dashboardStore";
import { MyProjectItemDetail, UserProjectShareType } from "@/types/dashboard.api";
import { G2_LAST_VIEWED_COOKIE_NAME } from "@/constants/g2";
import { getDashBoardState } from "@/redux/store";
import { useCookies } from "react-cookie";
import { AppReview } from "@capawesome/capacitor-app-review";
import { useCheckDevice } from "@/hooks/useCheckDevice";

dayjs.extend(customParseFormat);

const TAG = "[ShareTemplate]";

const AddCommentDiv = () => {
  const { t } = useTranslation("common");
  return (
    <div className="no-comments">
      <div className="no-comments-image">
        <Image
          src="/images/image_no_comments.png"
          alt="SearchCommentHistory"
          preview={false}
        />
      </div>
      <div className="main-txt">{t("Add your first comment")}</div>
      <div className="sub-txt">
        {t(
          "Leave feedback on this video. Comments you and other people post will show up here."
        )}
      </div>
    </div>
  );
};

const StyledRequestPermissionModal = styled(Modal)`
  .ant-modal-content {
    width: 400px;
  }

  .content {
    padding: 12px;
    font-family: Inter;
    font-size: 12px;
  }
`;

const RequestPermissionModal = ({
  open,
  onOk,
  onCancel,
}: {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation("common");

  return (
    <StyledRequestPermissionModal
      open={open}
      okText={t("Send Request")}
      onOk={onOk}
      onCancel={onCancel}
      title={<div>{t("Request Permission")}</div>}
    >
      <div className="content">
        {t(
          "You do not have permission to comment. Please request permission from the video owner. You will receive an email once the video owner approves your request."
        )}
      </div>
    </StyledRequestPermissionModal>
  );
};

export default function ShareTemplate({
  projectInfo,
  // projectShareMember,
  userProjectPermission,
  isMobile,
}: {
  projectInfo: MyProjectItemDetail;
  userProjectPermission: string;
  projectShareMember?: UserProjectShareType[];
  isMobile: boolean;
}) {
  const mediaRef = useRef<HTMLMediaElement>(null);
  const backgroundColor = useMemo(
    () => "#" + Math.floor(Math.random() * 16777215).toString(16),
    [],
  );
  const session = useSession();

  const router = useRouter();
  const { t } = useTranslation("common");
  const { share_id } = router.query;
  const [inputComment, setInputComment] = useState<string>("");
  const [currentTime, setCurrentTime] = useState<string>("00:00");
  const [isVideoTimer, setIsVideoTimer] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [checkVideoPlay, setCheckVideoPlay] = useState(false);
  const dispatch = useAppDispatch();
  const { isAndroidWebView, isIosWebView } = useCheckDevice();

  const { data: comments, refetch } = useGetCommentQuery(
    {
      projectId: projectInfo?._id,
    },
    {
      // pollingInterval: 1000 * 5,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
    },
  );

  const [createComment] = useCreateCommentMutation();

  const handleChangeComment = useCallback(
    (event: ChangeEventHandler<HTMLTextAreaElement>) => {
      setInputComment(event?.target?.value);
    },
    [],
  );

  const handleCreateComment = useCallback(async () => {
    setIsLoading(true);
    if (inputComment === "") {
      alert("Please insert a comment");
      setIsLoading(false);
      return;
    }

    try {
      const params = {
        content: inputComment,
        userId: session?.data?.user?.id,
        userName: session?.data?.user?.name,
        projectId: projectInfo?._id,
        // videoTime: currentTime,
      };

      if (isVideoTimer) {
        params.videoTime = currentTime;
      }

      const result = await createComment(params);
      messageApi.open({
        content: t("Successfully registered."),
        type: "success",
      });
      refetch();
    } catch (error) {
      const errorMessage =
        error?.message || "Create Comment Error. Please try again";
      messageApi.open({
        content: t(errorMessage),
        type: "error",
      });
      setIsLoading(false);
      setInputComment("");
    } finally {
      setIsLoading(false);
      setInputComment("");
    }
  }, [
    createComment,
    currentTime,
    inputComment,
    isVideoTimer,
    messageApi,
    projectInfo?._id,
    refetch,
    session?.data?.user?.id,
    session?.data?.user?.name,
    t,
  ]);

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !isLoading) {
        setIsLoading(true);
        await handleCreateComment();
        const target = event.target as HTMLTextAreaElement;
        target.blur();
        setIsLoading(false);
      }
    },
    [handleCreateComment, isLoading],
  );

  const handleVideoTime = useCallback((val: string) => {
    console.log(TAG, `handleVideoTime currentTime `, { val });

    const [minutes, seconds] = val.split(":").map(Number);
    const timer = minutes * 60 + seconds;

    if (mediaRef?.current) {
      mediaRef.current.currentTime = timer;
    }
  }, []);

  const handleEditComment = useCallback(
    async (val: {
      commentId: string;
      content: string;
      videoTime: string;
      type: string;
      projectId: string;
    }) => {
      const { commentId, content, videoTime, type, projectId } = val;

      const params = {
        commentId,
        content,
        videoTime,
        type,
        projectId,
      };

      const result = await createComment(params).unwrap();
      console.log("handleEditComment result: ", result);
      refetch();
    },
    [createComment, refetch],
  );

  const handleDeleteComment = useCallback(
    async (val: { projectId: string; commentId: string; type: string }) => {
      const { commentId, projectId, type } = val;
      console.log("val: ", val);
      const params = {
        commentId,
        projectId,
        type,
      };
      const result = await createComment(params).unwrap();
      refetch();
      console.log("result: ", result.data);
    },
    [createComment, refetch],
  );

  const handleNavigateToPricing = () => {
    window.open("https://www.deepbrain.io/pricing", "_blank");
  };

  const [openPermissionModal, setOpenPermissionModal] = useState(false);

  const handleCancelModal = () => {
    setOpenPermissionModal(false);
  };

  const handleOpenRequestPermission = () => {
    setOpenPermissionModal(true);
  };

  //
  const sendRequestPermission = useCallback(async () => {
    try {
      setIsLoading(true);

      const body = {
        templateName: "requestProjectCommentPermission",
        projectId: share_id,
        userId: session?.data?.user?.id,
        sharePermission: "comment",
        shareType: "user",
      };

      const result = await axios.post(
        "/api/workspace/send-request-permission-email",
        body,
      );

      console.log("sendRequestPermission result: ", result.data);
    } catch (error) {
      console.log("sendRequestPermission error: ", error);
    } finally {
      setIsLoading(false);
      notification.success({
        message: (
          <span style={{ color: "#ffffff" }}>
            {t("Permission has been requested successfully.")}
          </span>
        ),
        duration: 4,
        placement: "top",
        style: {
          width: "500px",
          backgroundColor: "#2bab62",
          position: "relative",
          borderRadius: "8px",
          padding: "10px 30px",
        },
        closeIcon: <span style={{ color: "#ffffff" }}>X</span>,
        icon: <CheckCircleOutlined style={{ color: "white" }} />,
      });
      handleCancelModal();
    }
  }, [session?.data?.user?.id, share_id, t]);

  const handlePlay = useCallback(() => {
    if (!checkVideoPlay) {
      setCheckVideoPlay(true);

      if (window?.dataLayer?.push) {
        window.dataLayer.push({
          event: "video_play",
          project_id: projectInfo.name,
        });
      }
    }
  }, [checkVideoPlay, projectInfo.name]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (mediaRef.current) {
        const _currentTime = mediaRef.current.currentTime;
        const minutes = Math.floor(_currentTime / 60)
          .toString()
          .padStart(2, "0");
        const seconds = Math.floor(_currentTime % 60)
          .toString()
          .padStart(2, "0");
        setCurrentTime(`${minutes}:${seconds}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  /**
   * G2 리뷰 관련 처리들...
   */
  const { accountUserInfo: userInfo } = useAppSelector((store) =>
    getDashBoardState(store),
  );

  const isLoggedIn = !!userInfo?.id;
  const isReviewedG2 = userInfo?.isNewG2reviewed || false;

  const cookieKey = `${G2_LAST_VIEWED_COOKIE_NAME}|${userInfo?.id}`;

  const [cookies] = useCookies([cookieKey]);

  const [isViewedG2State, setIsViewedG2State] = useState(false);

  const isViewedG2Cookie = !!cookies[cookieKey];

  const isViewedG2 = isViewedG2State || isViewedG2Cookie;

  const handlePause = useCallback(() => {
    console.log("비디오 일시정지");

    /**
     * 로그인 되어 있지 않으면 G2 리뷰 모달을 표시하지 않음
     */
    if (!isLoggedIn) {
      return;
    }

    /**
     * G2 리뷰를 이미 한 경우, 다시 표시하지 않음
     */
    if (isReviewedG2) {
      return;
    }

    /**
     * G2 리뷰 모달을 이미 본 경우, 다시 표시하지 않음
     */
    if (isViewedG2) {
      return;
    }

    setTimeout(() => {
      setIsViewedG2State(true);

      /**
       * 하이브리드 앱일 경우 G2리뷰 대신
       * 앱 마켓 리뷰 요청
       */
      if (isAndroidWebView || isIosWebView) {
        AppReview.requestReview()
      }
      else {
        dispatch(dashboardActions.setG2ReviewModalOpen({ isShow: true }));
      }
    }, 1000);
  }, [isLoggedIn, isReviewedG2, isViewedG2, dispatch, isAndroidWebView, isIosWebView]);

  useEffect(() => {
    const mediaElement = mediaRef.current;
    if (mediaElement) {
      mediaElement.addEventListener("play", handlePlay);
      mediaElement.addEventListener("pause", handlePause); // 일시정지 이벤트 추가

      return () => {
        mediaElement.removeEventListener("play", handlePlay);
        mediaElement.removeEventListener("pause", handlePause); // 일시정지 이벤트 제거
      };
    }
  }, [handlePlay, handlePause]);

  /**
   * 미디어 로드 상태 관련
   */
  const [isLoadedMedia, setIsLoadedMedia] = useState(false);

  const handleLoadedMedia = useCallback(() => {
    setIsLoadedMedia(true);
  }, []);

  const handleLoadStartMedia = useCallback(() => {
    setIsLoadedMedia(false);
  }, []);

  useEffect(() => {
    const media = mediaRef.current;

    if ((media?.readyState ?? 0) > 0) {
      setIsLoadedMedia(true);
    }

    if (media) {
      media.play();
    }

    return () => {
      if (media) {
        media.pause();
        media.remove();
      }
    };
  }, []);

  return (
    <ShareTemplateMain>
      {contextHolder}
      {!isMobile && (
        <div className="container">
          <div className="wrapper">
            <div className="title-container">
              <div className="title-wrapper">
                <Tooltip
                  placement="topRight"
                  title={<div>{projectInfo.name}</div>}
                >
                  <span className="title">{projectInfo.name}</span>
                </Tooltip>
                <div className="title-content">
                  <span>{projectInfo?.userId}</span>
                  <span className="date-text">
                    {dayjs(projectInfo?.createdAt, {
                      customParseFormat: "YYYY-MM-DD HH:mm",
                    }).format("MMM, D h:mm A")}
                  </span>
                </div>
              </div>
            </div>
            <div className="content">
              {projectInfo?.download_url && (
                <div className="media-wrapper">
                  <div className="media-wrapper-inner">
                    {projectInfo?.exportType === "audio" ? (
                      <div
                        className="audio-wrapper"
                        css={{
                          display: !isLoadedMedia ? "none" : "flex",
                        }}
                      >
                        <Image
                          className="thumbnail-wrapper"
                          preview={false}
                          alt="thumbnail"
                          src={
                            projectInfo?.thumbnailUrl ??
                            "/images/free_ai_avatar_video_generator.png"
                          }
                        />

                        <div className="audio-player-wrapper">
                          <audio
                            ref={mediaRef}
                            src={projectInfo?.download_url}
                            autoPlay={false}
                            controls
                            onLoadStart={handleLoadStartMedia}
                            onLoadedMetadata={handleLoadedMedia}
                          />

                          <div className="audio-player-description">
                            {t("This project has been exported as audio.")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <video
                        ref={mediaRef as RefObject<HTMLVideoElement>}
                        className="video-el"
                        src={projectInfo?.download_url}
                        autoPlay={false}
                        controls
                        onLoadStart={handleLoadStartMedia}
                        onLoadedMetadata={handleLoadedMedia}
                        css={{
                          display: !isLoadedMedia ? "none" : "block",
                        }}
                      />
                    )}

                    {!isLoadedMedia && (
                      <div className="skeleton-wrapper">
                        <Skeleton />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!projectInfo?.download_url && (
                <div className="media-wrapper">
                  <Image
                    className="thumbnail-wrapper"
                    preview={false}
                    alt="thumbnail"
                    src={
                      projectInfo?.thumbnailUrl ??
                      "/images/free_ai_avatar_video_generator.png"
                    }
                  />
                  <span className="video-info">
                    {t("This project has not yet been exported to video.")}
                  </span>
                </div>
              )}
              <div className="comment-wrapper">
                <div className="title">
                  <Typography.Title>{t("Comments")}</Typography.Title>
                </div>
                {userProjectPermission !== "view" && (
                  <>
                    <div className="user-input-box">
                      <div className="user">
                        <ProfileIcon
                          style={{
                            backgroundColor,
                            width: "30px",
                            height: "30px",
                          }}
                        >
                          {session?.data?.user?.id
                            ?.charAt(0)
                            ?.toLocaleUpperCase() || ""}
                        </ProfileIcon>
                        <span>{session?.data?.user?.name}</span>
                      </div>

                      <UserTextArea
                        // rows={3}
                        style={{ resize: "none" }}
                        value={inputComment}
                        textLength={inputComment.length ?? 0}
                        placeholder="Leave a comment"
                        onChange={handleChangeComment}
                        onKeyDown={handleKeyDown}
                        disabled={
                          userProjectPermission == "view" ? true : false
                        }
                      />
                      <div className="user-bottom">
                        <ClockCircleOutlined />
                        <span>{isVideoTimer ? currentTime : "00:00"}</span>
                        <Switch
                          size="small"
                          checked={isVideoTimer}
                          onChange={(checked: boolean) =>
                            setIsVideoTimer(checked)
                          }
                        />
                        <Button
                          className="comment-button"
                          disabled={
                            userProjectPermission == "view" ? true : false
                          }
                          onClick={handleCreateComment}
                          loading={isLoading}
                        >
                          {t("Comment")}
                        </Button>
                      </div>
                    </div>
                    <div className="comments-list">
                      {comments?.map((comment: CommentType, index: number) => (
                        <ShareComment
                          key={comment._id + index}
                          comment={comment}
                          userProjectPermission={userProjectPermission}
                          handleVideoTime={handleVideoTime}
                          handleEditComment={handleEditComment}
                          handleDeleteComment={handleDeleteComment}
                        />
                      ))}
                      {comments?.length === 0 && <AddCommentDiv />}
                    </div>
                  </>
                )}

                {/* {userProjectPermission === "view" && <AddCommentDiv />} */}
                {userProjectPermission === "view" && (
                  <>
                    <AddCommentDiv />
                    <Button
                      className="comment-btn"
                      onClick={handleOpenRequestPermission}
                    >
                      {t("Comment")}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <Button
              className="create-account-button"
              onClick={handleNavigateToPricing}
            >
              {session.status === "unauthenticated"
                ? t("Create Account")
                : t("Checkout the benefits")}
            </Button>
          </div>
        </div>
      )}
      {isMobile && (
        <div className="mobile-container">
          <div className="wrapper">
            <div className="title-container">
              <div className="title-wrapper">
                <Tooltip
                  placement="topRight"
                  title={<div>{projectInfo.name}</div>}
                >
                  <span className="title">{projectInfo.name}</span>
                </Tooltip>
                <div className="title-content">
                  <span>{projectInfo?.userId}</span>
                  <span className="date-text">
                    {dayjs(projectInfo?.createdAt, {
                      customParseFormat: "YYYY-MM-DD HH:mm",
                    }).format("MMM, D h:mm A")}
                  </span>
                </div>
              </div>
            </div>
            <div className="content">
              {projectInfo?.download_url && (
                <div className="media-wrapper">
                  <div className="media-wrapper-inner">
                    {projectInfo?.exportType === "audio" ? (
                      <div
                        className="audio-wrapper"
                        css={{
                          display: !isLoadedMedia ? "none" : "flex",
                        }}
                      >
                        <Image
                          className="thumbnail-wrapper"
                          preview={false}
                          alt="thumbnail"
                          src={
                            projectInfo?.thumbnailUrl ??
                            "/images/free_ai_avatar_video_generator.png"
                          }
                          wrapperStyle={{
                            width: "100%",
                            height: "100%",
                          }}
                        />

                        <div className="audio-player-wrapper">
                          <audio
                            ref={mediaRef}
                            src={projectInfo?.download_url}
                            autoPlay={false}
                            controls
                            onLoadStart={handleLoadStartMedia}
                            onLoadedMetadata={handleLoadedMedia}
                          />

                          <div className="audio-player-description">
                            {t("This project has been exported as audio.")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <video
                        ref={mediaRef as RefObject<HTMLVideoElement>}
                        className="video-el"
                        src={projectInfo?.download_url}
                        autoPlay={false}
                        controls
                        onLoadStart={handleLoadStartMedia}
                        onLoadedMetadata={handleLoadedMedia}
                        css={{
                          display: !isLoadedMedia ? "none" : "block",
                        }}
                      />
                    )}

                    {!isLoadedMedia && (
                      <div className="skeleton-wrapper">
                        <Skeleton />
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!projectInfo?.download_url && (
                <div className="media-wrapper">
                  <Image
                    className="thumbnail-wrapper"
                    preview={false}
                    alt="thumbnail"
                    src={
                      projectInfo?.thumbnailUrl ??
                      "/images/free_ai_avatar_video_generator.png"
                    }
                  />
                  <span className="video-info">
                    {t("This project has not yet been exported to video.")}
                  </span>
                </div>
              )}
              <div className="comment-wrapper">
                <div className="title">
                  <Typography.Title>{t("Comments")}</Typography.Title>
                </div>
                {userProjectPermission !== "view" && (
                  <>
                    <div className="user-input-box">
                      <div className="user">
                        <ProfileIcon
                          style={{
                            backgroundColor,
                            width: "30px",
                            height: "30px",
                          }}
                        >
                          {session?.data?.user?.id
                            ?.charAt(0)
                            ?.toLocaleUpperCase() || ""}
                        </ProfileIcon>
                        <span>{session?.data?.user?.name}</span>
                      </div>

                      <UserTextArea
                        // rows={3}
                        style={{ resize: "none" }}
                        value={inputComment}
                        textLength={inputComment.length ?? 0}
                        placeholder="Leave a comment"
                        onChange={handleChangeComment}
                        onKeyDown={handleKeyDown}
                        disabled={
                          userProjectPermission == "view" ? true : false
                        }
                      />
                      <div className="user-bottom">
                        <ClockCircleOutlined />
                        <span>{isVideoTimer ? currentTime : "00:00"}</span>
                        <Switch
                          size="small"
                          checked={isVideoTimer}
                          onChange={(checked: boolean) =>
                            setIsVideoTimer(checked)
                          }
                        />
                        <Button
                          className="comment-button"
                          disabled={
                            userProjectPermission == "view" ? true : false
                          }
                          onClick={handleCreateComment}
                          loading={isLoading}
                        >
                          {t("Comment")}
                        </Button>
                      </div>
                    </div>
                    <div className="comments-list">
                      {comments?.map((comment: CommentType, index: number) => (
                        <ShareComment
                          key={comment._id + index}
                          comment={comment}
                          userProjectPermission={userProjectPermission}
                          handleVideoTime={handleVideoTime}
                          handleEditComment={handleEditComment}
                          handleDeleteComment={handleDeleteComment}
                        />
                      ))}
                      {comments?.length === 0 && <AddCommentDiv />}
                    </div>
                  </>
                )}

                {/* {userProjectPermission === "view" && <AddCommentDiv />} */}
                {userProjectPermission === "view" && (
                  <>
                    <AddCommentDiv />
                    <Button
                      className="comment-btn"
                      onClick={handleOpenRequestPermission}
                    >
                      {t("Comment")}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {/* <Button
              className="create-account-button"
              onClick={handleNavigateToPricing}
            >
              {session.status === "unauthenticated"
                ? t("Create Account")
                : t("Checkout the benefits")}
            </Button> */}
          </div>
        </div>
      )}
      <RequestPermissionModal
        open={openPermissionModal}
        onOk={sendRequestPermission}
        onCancel={handleCancelModal}
      />
    </ShareTemplateMain>
  );
}


const Skeleton = styled.div`
  width: 100%;
  height: 100%;

  background-image: linear-gradient(
    150deg,
    #F0F1F3 0%,
    #F0F1F3 40%,
    #ffffff 50%,
    #F0F1F3 60%,
    #F0F1F3 100%
  );
  background-size: 400% 100%;
  background-position-y: 0%;

  animation: media-skeleton-animation 2s infinite;

  @keyframes media-skeleton-animation {
    0% {
      background-position-x: 100%;
    }
    100% {
      background-position-x: 0%;
    }
  }
`;
